import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import "./Address.css";
import {
  allLocations,
  getLocations,
  initialLocation,
  setInitialAddress,
  locationsStatus, // add selector in slice: 'idle' | 'loading' | 'succeeded' | 'failed'
  locationsError // add selector in slice: string | null
} from "../../features/locations/locationSlice";
// import { uid } from "../../features/user/userSlice";
import { Modal, Button, Loader, SelectPicker, Message } from "rsuite";
import { getDataForAddressSelect } from "../../util/util";
import * as turf from "@turf/turf";
import Footer from "../Footer/Footer";

/**
 * Component that displays the Address page
 *
 * @return {JSX.Element} Address component JSX
 */
function SelectLocation() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const locations = useSelector(allLocations);
  const selectedLocation = useSelector(initialLocation);
  // const userId = useSelector(uid);
  const status = useSelector(locationsStatus); // ensure these exist in the slice
  const error = useSelector(locationsError);

  const [open, setOpen] = useState(false);
  const [locationId, setLocationId] = useState(null);
  const [locationName, setLocationName] = useState("");
  const [showLoader, setShowLoader] = useState(false);

  // Load locations once when needed and open modal
  useEffect(() => {
    // If a location is already selected (e.g., persisted), navigate directly.
    if (selectedLocation?.id) {
      navigate("/products/ALL");
      return;
    }
    // Otherwise, ensure locations are loaded and open modal.
    if (status === "idle") {
      dispatch(getLocations());
    }
    setOpen(true);
  }, [dispatch, navigate, selectedLocation?.id, status]);

  // Derived picker data
  const pickerData = useMemo(
    () => getDataForAddressSelect(locations),
    [locations]
  );

  // Geolocation-based preselect: run once after locations are loaded
  useEffect(() => {
    if (!open) return;
    if (status !== "succeeded") return;
    if (!("geolocation" in navigator)) return;

    let cancelled = false;

    const tryGeolocate = () => {
      const options = {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 60000
      };
      navigator.geolocation.getCurrentPosition(
        (position) => {
          if (cancelled) return;
          const { latitude, longitude } = position.coords;

          // IMPORTANT: Turf expects [lng, lat]
          const userPoint = turf.point([longitude, latitude]); // [lng, lat] [web:2][web:8]

          for (const location of locations) {
            const coordsSrc = location?.geofences?.coordinates;
            if (!Array.isArray(coordsSrc) || coordsSrc.length < 3) continue;

            // Map Firestore-like coords to [lng, lat]
            const ring = coordsSrc.map((c) => [c._long, c._lat]); // [lng, lat] [web:2][web:8]

            // Ensure ring closure
            if (ring.length >= 3) {
              const first = ring[0];
              const last = ring[ring.length - 1];
              if (first[0] !== last[0] || first[1] !== last[1]) {
                ring.push([...first]);
              }
            }

            // Build Polygon feature as GeoJSON
            const areaPolygon = turf.polygon([ring]); // [[ring]] [web:2][web:14]

            // Test membership
            const inside = turf.booleanPointInPolygon(userPoint, areaPolygon, {
              ignoreBoundary: false
            }); // [web:2][web:8]

            if (inside) {
              setLocationId(location.id);
              setLocationName(location.apartmentName || "");
              break;
            }
          }
        },
        (_err) => {
          // Fail silently; user can pick manually
        },
        options
      );
    };

    tryGeolocate();

    return () => {
      cancelled = true;
    };
  }, [locations, open, status]);

  const handleOptionChange = (value) => {
    setLocationId(value);
    const found = locations.find((l) => l.id === value);
    setLocationName(found?.apartmentName || "");
  };

  const handleContinue = () => {
    if (!locationId) return;
    const selectedOption = locations.find((option) => option.id === locationId);
    if (!selectedOption) return;
    setShowLoader(true);
    // If setInitialAddress is async, consider awaiting or listening in effect below
    dispatch(setInitialAddress(selectedOption));
  };

  // Navigate only after Redux reflects a selected location
  useEffect(() => {
    if (selectedLocation?.id) {
      navigate("/products/ALL");
    }
  }, [navigate, selectedLocation?.id]);

  return (
    <>
      <Modal backdrop="static" open={open}>
        <Modal.Body>
          <h4>Welcome to Green Yogi!</h4>
          <span className="select-location-content">
            We&apos;re excited to have you shop with us for fresh and delicious
            fruits and vegetables. To provide you with the best service, please
            select your delivery location from the options below:
          </span>

          {status === "loading" ? (
            <Loader size="md" />
          ) : status === "failed" ? (
            <Message type="error" showIcon>
              Could not load locations. Please try again. {error || ""}{" "}
            </Message>
          ) : (
            <SelectPicker
              placeholder={locationId ? locationName : "Select Location"}
              data={pickerData}
              cleanable={false}
              className="select-apartment"
              value={locationId}
              onChange={handleOptionChange}
            />
          )}

          <span className="select-location-content">
            Don&apos;t see your location listed? No worries! We&apos;re
            constantly expanding our reach. Feel free to contact our customer
            support for more information.
          </span>
          <span className="select-location-content">
            Choose your location and start exploring our wide variety of
            farm-fresh products. Thank you for choosing Green Yogi!
          </span>
        </Modal.Body>
        <Modal.Footer>
          {locationId != null ? (
            <Button
              onClick={handleContinue}
              appearance="primary"
              disabled={showLoader}
            >
              {showLoader ? <Loader /> : "Continue"}
            </Button>
          ) : null}
        </Modal.Footer>
      </Modal>
      <Footer tab={"location"} />
    </>
  );
}

export default SelectLocation;
